import type { ClientServiceProfileFieldsFragment } from "../../__generated__/graphql";
import { babysitterTasksOptions } from "../baby-profile-form/lib";

export type ServiceProfileType = "pet" | "elder" | "baby";

export const ServiceProfileTypeMap: Record<ServiceProfileType, string> = {
  pet: "Pet Care",
  elder: "Eldercare",
  baby: "Child",
};

export const ServiceProfileServiceIdMap: Record<ServiceProfileType, string> = {
  pet: "21",
  elder: "33",
  baby: "17",
};

export const SERVICE_PROFILE_TYPES = Object.keys(ServiceProfileTypeMap) as ServiceProfileType[];

export const SERVICE_PROFILE_ROOT_PATH = "/profile/service-profile";
export const serviceProfileListPath = (type: ServiceProfileType) =>
  `${SERVICE_PROFILE_ROOT_PATH}/${type}`;
export const serviceProfileCreatePath = (type: ServiceProfileType) =>
  `${SERVICE_PROFILE_ROOT_PATH}/${type}/create`;
export const serviceProfileEditPath = (type: ServiceProfileType, id: string) =>
  `${SERVICE_PROFILE_ROOT_PATH}/${type}/${id}`;

export type ServiceProfileItem = {
  id: string;
  name: string;
  visibleAttributes: string[];
  isDefault: boolean;
  type: ServiceProfileType;
};

const capitalizeWords = (value: string) =>
  value.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const TASK_LABELS: Record<string, string> = Object.fromEntries(
  babysitterTasksOptions.map((option) => [option.key, option.label]),
);

function visibleKeys(type: ServiceProfileType): string[] {
  switch (type) {
    case "pet":
      return ["type", "breed", "age", "gender"];
    case "elder":
      return ["age", "weight"];
    case "baby":
      return ["name", "age"];
  }
}

export function visibleAttributesValue(attributes: unknown, type: ServiceProfileType): string[] {
  if (typeof attributes !== "object" || attributes === null) return [];
  const record = attributes as Record<string, unknown>;

  return visibleKeys(type).reduce<string[]>((acc, key) => {
    const value = record[key];

    if (key === "babysitter_tasks" && typeof value === "object" && value !== null) {
      const selected = Object.entries(value)
        .filter(([, isSelected]) => isSelected === true)
        .map(([taskKey]) => TASK_LABELS[taskKey] ?? taskKey)
        .slice(0, 2);
      if (selected.length > 0) acc.push(selected.join(" • "));
    } else if (Array.isArray(value)) {
      acc.push(value.slice(0, 2).join(" • "));
    } else if (typeof value === "string") {
      acc.push(capitalizeWords(value));
    }

    return acc;
  }, []);
}

export function mapServiceProfileToItem(
  profile: ClientServiceProfileFieldsFragment,
  type: ServiceProfileType,
): ServiceProfileItem {
  return {
    id: profile.id,
    name: capitalizeWords(profile.name),
    visibleAttributes: visibleAttributesValue(
      { name: profile.name, ...(profile.profileAttributes as Record<string, unknown>) },
      type,
    ),
    isDefault: profile.primary,
    type,
  };
}

const MAX_PER_TYPE = 3;

/** Whether the client can still add a profile of this type (max 3 per type; pet is 3 cats + 3 dogs). */
export function canCreateMore(type: ServiceProfileType, items: ServiceProfileItem[]): boolean {
  if (type === "pet") {
    const cats = items.filter((item) => item.visibleAttributes[0] === "Cat").length;
    const dogs = items.filter((item) => item.visibleAttributes[0] === "Dog").length;
    return cats < MAX_PER_TYPE || dogs < MAX_PER_TYPE;
  }
  return items.length < MAX_PER_TYPE;
}

export function createLimitCaption(type: ServiceProfileType): string {
  return type === "pet"
    ? "You can have up to 3 cat profiles and 3 dog profiles."
    : "You can have up to 3 profiles.";
}
